import React from 'react'

import { BreakpointArray } from '@thg-commerce/gravity-theme'

import { CarouselItem } from '../styles'
import {
  CarouselReducerActions,
  CarouselReducerActionTypes,
  CarouselState,
} from '../types'

interface CarouselItemsProps {
  items: JSX.Element[]
  state: CarouselState
  containerRef: React.RefObject<HTMLDivElement>
  carouselItemsRef: React.MutableRefObject<(HTMLLIElement | null)[]>
  dispatch: (value: CarouselReducerActions) => void
  hideSlidePreview?: boolean
  isZoomModal?: boolean
  zoomClickable?: boolean
  inactiveSlideFullOpacity?: boolean
  itemsPerSlide: number | number[]
  itemCount: number
  overflow?: BreakpointArray<string>
  enableSlidePreview?: boolean
  enableDualSidePreview?: boolean
  fullWidthCarousel?: boolean
  zoomOnClick?: (index: number) => void
  hideControlsOnSingleSlide?: boolean
  itemGapSpacing?: number
  enableWrapAround?: boolean
}

export const CarouselItems: React.FC<CarouselItemsProps> = ({
  items,
  state,
  containerRef,
  carouselItemsRef,
  dispatch,
  hideSlidePreview,
  isZoomModal,
  zoomClickable,
  inactiveSlideFullOpacity,
  itemsPerSlide,
  overflow,
  enableSlidePreview,
  enableDualSidePreview,
  fullWidthCarousel,
  zoomOnClick,
  hideControlsOnSingleSlide,
  itemGapSpacing,
  enableWrapAround,
}) => {
  const maxIndex = items.length - 1

  const handleItemFocus = React.useCallback(
    (index: number) => {
      const next = Math.floor(index / state.itemsPerSlide)

      requestAnimationFrame(() => {
        containerRef.current && (containerRef.current.scrollLeft = 0)

        if (next !== state.currentSlide) {
          dispatch({
            type:
              next > state.currentSlide
                ? CarouselReducerActionTypes.SlideRight
                : CarouselReducerActionTypes.SlideLeft,
          })
        }
      })
    },
    [state.currentSlide, state.itemsPerSlide],
  )

  const handleZoomOnClick = () => zoomOnClick && zoomOnClick(0 || 0)
  const hideCarouselControls = hideControlsOnSingleSlide && !state.showControls

  const wrapDuplicates = enableWrapAround
    ? [
        <CarouselItem
          itemsPerSlide={itemsPerSlide}
          itemCount={items.length}
          inactiveSlideFullOpacity={inactiveSlideFullOpacity}
          active={false}
          hideSlidePreview={hideSlidePreview}
          isZoomModal={isZoomModal}
          zoomClickable={zoomClickable}
          onClick={handleZoomOnClick}
          controlsHidden={hideCarouselControls}
          style={{
            height: 'auto',
          }}
          key={maxIndex}
          onFocus={() => handleItemFocus(maxIndex)}
          data-testid={`carousel-item-${maxIndex}`}
          enableSlidePreview={enableSlidePreview}
          enableDualSidePreview={enableDualSidePreview}
          spacing={itemGapSpacing}
          overflow={overflow}
          fullWidthCarousel={fullWidthCarousel}
          isVideo={items[maxIndex].type === 'video'}
        >
          {items[maxIndex]}
        </CarouselItem>,
        <CarouselItem
          itemsPerSlide={itemsPerSlide}
          itemCount={items.length}
          inactiveSlideFullOpacity={inactiveSlideFullOpacity}
          active={false}
          hideSlidePreview={hideSlidePreview}
          isZoomModal={isZoomModal}
          zoomClickable={zoomClickable}
          onClick={handleZoomOnClick}
          controlsHidden={hideCarouselControls}
          style={{
            height: 'auto',
          }}
          key={0}
          onFocus={() => handleItemFocus(0)}
          data-testid={`carousel-item-${0}`}
          enableSlidePreview={enableSlidePreview}
          enableDualSidePreview={enableDualSidePreview}
          spacing={itemGapSpacing}
          overflow={overflow}
          fullWidthCarousel={fullWidthCarousel}
          isVideo={items[0].type === 'video'}
        >
          {items[0]}
        </CarouselItem>,
        <CarouselItem
          itemsPerSlide={itemsPerSlide}
          itemCount={items.length}
          inactiveSlideFullOpacity={inactiveSlideFullOpacity}
          active={false}
          hideSlidePreview={hideSlidePreview}
          isZoomModal={isZoomModal}
          zoomClickable={zoomClickable}
          onClick={handleZoomOnClick}
          controlsHidden={hideCarouselControls}
          style={{
            height: 'auto',
          }}
          key={1}
          onFocus={() => handleItemFocus(1)}
          data-testid={`carousel-item-${1}`}
          enableSlidePreview={enableSlidePreview}
          enableDualSidePreview={enableDualSidePreview}
          spacing={itemGapSpacing}
          overflow={overflow}
          fullWidthCarousel={fullWidthCarousel}
          isVideo={items[1].type === 'video'}
        >
          {items[1]}
        </CarouselItem>,
      ]
    : [null, null, null]

  return (
    <React.Fragment>
      {wrapDuplicates[0]}
      {items.map((item, index) => {
        const maxIndex = Math.min(
          state.itemsPerSlide * (state.currentSlide + 1),
          items.length,
        )
        return (
          <CarouselItem
            ref={(item) => (carouselItemsRef.current[index] = item)}
            itemsPerSlide={itemsPerSlide}
            itemCount={items.length}
            inactiveSlideFullOpacity={inactiveSlideFullOpacity}
            active={index + 1 <= maxIndex}
            hideSlidePreview={hideSlidePreview}
            isZoomModal={isZoomModal}
            zoomClickable={zoomClickable}
            onClick={handleZoomOnClick}
            controlsHidden={hideCarouselControls}
            style={{
              height: 'auto',
            }}
            key={index}
            onFocus={() => handleItemFocus(index)}
            data-testid={`carousel-item-${index}`}
            enableSlidePreview={enableSlidePreview}
            enableDualSidePreview={enableDualSidePreview}
            spacing={itemGapSpacing}
            overflow={overflow}
            fullWidthCarousel={fullWidthCarousel}
            isVideo={item.type === 'video'}
          >
            {item}
          </CarouselItem>
        )
      })}
      {wrapDuplicates[1]}
      {wrapDuplicates[2]}
    </React.Fragment>
  )
}
